import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/auth';
import { api, headlessPost } from '@/lib/api';
import type { User } from './types';

// --- Passwordless login (allauth headless app client) ----------------------
//
// Flow: request a one-time code by email → confirm the code → exchange the
// headless session for the DRF token the rest of the API uses. The session
// token (X-Session-Token) is threaded through the steps; only the final DRF
// token is persisted.

const REQUEST_CODE = '/_allauth/app/v1/auth/code/request';
const CONFIRM_CODE = '/_allauth/app/v1/auth/code/confirm';
const TOKEN_EXCHANGE = '/api/auth/token/';

export class LoginError extends Error {}

/** Step 1: request a login code. Returns the flow session token. By design the
 *  response is identical whether or not the email is whitelisted (no
 *  enumeration leak) — the UI always advances to code entry. */
export function useRequestCode() {
  return useMutation({
    mutationFn: async (email: string): Promise<string> => {
      const res = await headlessPost(REQUEST_CODE, { email });
      const token = res.data.meta?.session_token;
      if (!token) throw new LoginError('Could not start sign-in. Please try again.');
      return token;
    },
  });
}

/** Step 2: confirm the code, then exchange the authenticated session for a DRF
 *  token and store it. */
export function useConfirmCode() {
  const setToken = useAuth((s) => s.setToken);
  return useMutation({
    mutationFn: async ({ code, sessionToken }: { code: string; sessionToken: string }) => {
      const confirm = await headlessPost(CONFIRM_CODE, { code }, sessionToken);
      if (!confirm.ok) throw new LoginError('That code is invalid or expired.');
      const authedToken = confirm.data.meta?.session_token ?? sessionToken;

      const exchange = await headlessPost(TOKEN_EXCHANGE, {}, authedToken);
      const key = (exchange.data as { key?: string }).key;
      if (!exchange.ok || !key) throw new LoginError('Sign-in could not be completed.');
      setToken(key);
    },
  });
}

export function useUser() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['auth', 'user'],
    queryFn: () => api.get<User>('/api/auth/user/'),
    enabled: isAuthenticated,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    // Name and username are editable; email is the login identity (read-only)
    // and there is no password. The server refuses a username for reasons the
    // input alone doesn't show (taken, reserved, held, rate-limited), so
    // callers should read `fieldErrors(err).username` rather than assuming a
    // failure means the request was malformed.
    mutationFn: (patch: { first_name?: string; last_name?: string; username?: string }) =>
      api.patch<User>('/api/auth/user/', patch),
    // Publish ONLY the keys this request sent.
    //
    // Writing the whole response was wrong: for a field the request did not
    // send it is a snapshot the server happened to take, which a concurrent
    // change may already have superseded, and publishing it wholesale pushed
    // that stale data to every consumer.
    //
    // Publishing NOTHING was also wrong, and that is what this replaces: with
    // only the background re-read able to touch the cache, a read that fails
    // or hangs left it stale indefinitely -- the form reported success and
    // updated itself while the account header and every other consumer kept
    // showing the old username.
    //
    // The keys in `patch` are exactly what the server was asked to change, so
    // the response is authoritative for those and silent on the rest.
    onSuccess: (saved, patch) => {
      qc.setQueryData<User>(['auth', 'user'], (cached) => {
        if (!cached) return saved;
        const sent = Object.keys(patch) as (keyof User)[];
        return sent.reduce<User>(
          (merged, key) => (key in saved ? { ...merged, [key]: saved[key] } : merged),
          cached,
        );
      });
    },
    // Re-read after every attempt. The response is authoritative for the
    // fields this request SENT; for any it omitted it is only a snapshot taken
    // at the server, which a refetch landing mid-flight may already have
    // superseded. The re-read also discards any GET that read the row BEFORE
    // this PATCH and resolved after it, since the newer fetch wins.
    //
    // NOT returned. TanStack awaits a hook-level onSettled before running the
    // per-call onSuccess/onError, so returning this promise chains the whole
    // save behind an extra GET: Save stays disabled, the success toast and any
    // username rejection are withheld, and if that GET hangs the form sits in
    // "Saving..." forever over a PATCH that already completed. Reconciliation
    // is not part of the save.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'user'] });
    },
  });
}

export interface UsernameAvailability {
  available: boolean;
  reason: 'invalid' | 'reserved' | 'taken' | 'held' | null;
}

/**
 * Live availability for the Account screen. Advisory only — it can report
 * available and the save still lose the race, which the server answers with a
 * 409. Debounced by the caller; the endpoint is rate-limited at 30/min.
 *
 * Disabled for an empty value so the field doesn't query on mount.
 */
export function useUsernameAvailable(value: string) {
  return useQuery({
    queryKey: ['auth', 'username-available', value],
    queryFn: () =>
      api.get<UsernameAvailability>(
        `/api/auth/username-available/?u=${encodeURIComponent(value)}`,
      ),
    enabled: value.length > 0,
    // The answer is a fact about the world, not about this user; a short cache
    // keeps keystroke-driven refetches down without going stale enough to
    // matter (the save is authoritative either way).
    staleTime: 10_000,
    retry: false,
  });
}
